import React, { useState, useEffect, forwardRef, useRef } from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import { splitStyleProps } from '../../utils/splitStyleProps'
import { usePrevious } from '../../hooks/usePrevious'
import { StackProps, Stack } from '../Stack'
import { TextInput, TextInputProps } from '../TextInput'

dayjs.extend(customParseFormat)

export interface DayMonthYearInputProps extends Omit<StackProps, 'order'> {
  order?: 'month-day-year' | 'day-month-year' | 'year-month-day'
  dayProps?: TextInputProps
  monthProps?: TextInputProps
  yearProps?: TextInputProps
  value?: string
  defaultValue?: string
  onChangeValue?: (date: string) => void
  name?: string
  dateFormat?: string
  isInvalid?: boolean
}

export const DayMonthYearInput = forwardRef(
  (
    {
      order = 'day-month-year',
      name,
      dayProps,
      monthProps,
      yearProps,
      value,
      defaultValue,
      onChangeValue,
      dateFormat = 'YYYY-MM-DD',
      isInvalid,
      ...restProps
    }: DayMonthYearInputProps,
    ref,
  ) => {
    const previousDefaultValue = usePrevious(defaultValue)

    const getDefaultValues = () => {
      const initialValue = value || defaultValue
      const initialDate = initialValue ? dayjs(initialValue, dateFormat) : null
      const isInitialDateValid = initialDate && dayjs(initialDate).isValid()

      return {
        isValid: isInitialDateValid,
        day: isInitialDateValid ? String(initialDate.get('date')) : null,
        month: isInitialDateValid ? String(initialDate.get('month') + 1) : null,
        year: isInitialDateValid ? String(initialDate.get('year')) : null,
      }
    }

    /* If the defaultValue updates, update the internal state. */
    useEffect(() => {
      if (defaultValue && defaultValue != previousDefaultValue) {
        const newValues = getDefaultValues()

        if (newValues.isValid) {
          setDay(newValues.day)
          setMonth(newValues.month)
          setYear(newValues.year)
        }
      }
    }, [defaultValue])

    const defaultValues = getDefaultValues()

    const [day, setDay] = useState<string>(defaultValues.day)
    const [month, setMonth] = useState<string>(defaultValues.month)
    const [year, setYear] = useState<string>(defaultValues.year)

    const monthRef = useRef(null)
    const yearRef = useRef(null)

    const [rootProps, inputProps] = splitStyleProps(restProps)

    const getDateString = (): string | null => {
      const _day = day?.padStart(2, '0')
      const _month = month?.padStart(2, '0')

      let _date = dateFormat
        .replace('DD', _day)
        .replace('MM', _month)
        .replace('YYYY', year)
        .replace('YY', year?.slice?.(-2))

      const isValid = dayjs(_date, dateFormat).isValid()

      if (!isValid) _date = null

      return _date
    }

    const [date, setDate] = useState(getDateString())

    useEffect(() => {
      if (!month) return // Quick fix to ensure `onChangeValue` isn't run on render. Can be improved!

      const _date = getDateString()

      setDate(_date)
    }, [day, month, year])

    /* TODO: Better tweaks so onChangeValue always returns a more reliable answer */
    useEffect(() => {
      if (!date) return // Quick fix to ensure `onChangeValue` isn't run on render. Can be improved!

      onChangeValue && onChangeValue(date)
    }, [date])

    const handleChangeDay = value => {
      setDay(value)

      if (value.length >= 2 || value >= 4) {
        monthRef.current?.focus?.()
      }
    }

    const handleChangeMonth = value => {
      setMonth(value)

      if (value.length >= 2 || value >= 2) {
        yearRef.current?.focus?.()
      }
    }

    const inputs = {
      day: (
        <TextInput
          placeholder="DD"
          type="tel"
          value={day}
          onChangeValue={handleChangeDay}
          name={name ? `${name}-day` : null}
          w="4rem"
          ref={ref}
          maxLength={2}
          max={31}
          isInvalid={isInvalid}
          {...dayProps}
        />
      ),
      month: (
        <TextInput
          placeholder="MM"
          type="tel"
          value={month}
          onChangeValue={handleChangeMonth}
          name={name ? `${name}-month` : null}
          w="4rem"
          maxLength={2}
          max={12}
          ref={monthRef}
          isInvalid={isInvalid}
          {...monthProps}
        />
      ),
      year: (
        <TextInput
          placeholder="YYYY"
          type="tel"
          value={year}
          onChangeValue={value => setYear(value)}
          name={name ? `${name}-year` : null}
          w="6rem"
          maxLength={4}
          max={new Date().getFullYear() - 17}
          ref={yearRef}
          isInvalid={isInvalid}
          {...yearProps}
        />
      ),
    }

    const splitOrder = order.split('-')

    return (
      <Stack isInline spacing={3} {...rootProps}>
        <input {...inputProps} name={name} value={date} ref={ref} type="hidden" />
        {splitOrder.map(input => inputs[input])}
      </Stack>
    )
  },
)

export default DayMonthYearInput
